/**
 * Maps /naighbourhood-poi-description response to UI component format
 */

import type {
  NeighbourhoodInsightsData,
  StatCard,
} from '@/components/Neighbourhood/neighbourhood-insights-types';
import type { NeighbourhoodPoiDescriptionResponse } from '@/helpers/Neighbourhood/neighbourhoodPOITypes';

export function transformPOIResponseToComponentFormat(
  response: NeighbourhoodPoiDescriptionResponse,
): NeighbourhoodInsightsData {
  const { data } = response;

  // Defensive: Ensure highlightsSummary exists
  const highlights = data?.highlightsSummary || 'Explore this neighbourhood';

  // Defensive: Ensure lifestyleDNA array exists and has items
  const lifestyleDNA = (data?.lifestyleDNA || []).map((item) => ({
    name: item?.label || 'Unknown',
    icon: mapLifestyleDNAIconToComponent(item?.icon || '', item?.label || ''),
    description: item?.evidence || '',
  }));
  // Defensive: Ensure keyPlaces and places array exist
  const keyPlaces = (data?.keyPlaces?.places || []).map((place) => ({
    name: place?.name || 'Unknown',
    distance: place?.distance || 'N/A',
    icon: mapKeyPlaceIconToComponent(place?.icon || ''),
    walkable: place?.walkable ?? false,
  }));

  // Build persona-specific stats
  const statsCard = buildPersonaStats(data);

  return {
    highlights,
    lifestyleDNA,
    keyPlaces,
    statsCard,
  };
}

// Constants for data validation
const NA_VALUE = 'N/A';
const UNKNOWN_VALUE = 'unknown';

/**
 * Helper to check if a value is valid (not N/A or unknown)
 */
const isValidValue = (value: string): boolean => {
  const trimmedValue = value.trim();
  return trimmedValue !== '' && trimmedValue !== NA_VALUE && trimmedValue !== UNKNOWN_VALUE;
};

/**
 * Persona-specific stat builders using strategy pattern
 */
interface PersonaStatsBuilder {
  buildStats(data: NeighbourhoodPoiDescriptionResponse['data']): {
    stat1: StatCard;
    stat2: StatCard;
    stat3: StatCard;
  };
}

const familyStatsBuilder: PersonaStatsBuilder = {
  buildStats: (data) => {
    const metrics =
      data.metrics as import('@/helpers/Neighbourhood/neighbourhoodPOITypes').FamilyMetrics;
    return {
      stat1: {
        label: metrics.zoneType.label,
        value: isValidValue(metrics.zoneType.value) ? metrics.zoneType.value : NA_VALUE,
        subtitle: '',
      },
      stat2: {
        label: metrics.parksNearby.label,
        value: isValidValue(metrics.parksNearby.value) ? metrics.parksNearby.value : NA_VALUE,
        subtitle: '',
      },
      stat3: {
        label: metrics.nearestSchool.label,
        value: isValidValue(metrics.nearestSchool.value) ? metrics.nearestSchool.value : NA_VALUE,
        subtitle: '',
      },
    };
  },
};

const professionalStatsBuilder: PersonaStatsBuilder = {
  buildStats: (data) => {
    const metrics =
      data.metrics as import('@/helpers/Neighbourhood/neighbourhoodPOITypes').ProfessionalMetrics;
    return {
      stat1: {
        label: metrics.commuteToCenter.label,
        value: isValidValue(metrics.commuteToCenter.value)
          ? metrics.commuteToCenter.value
          : NA_VALUE,
        subtitle: '',
      },
      stat2: {
        label: metrics.taxMultiplier.label,
        value: isValidValue(metrics.taxMultiplier.value) ? metrics.taxMultiplier.value : NA_VALUE,
        subtitle: '',
      },
      stat3: {
        label: metrics.connectivitySpeed.label,
        value: isValidValue(metrics.connectivitySpeed.value)
          ? metrics.connectivitySpeed.value
          : NA_VALUE,
        subtitle: '',
      },
    };
  },
};

const studentStatsBuilder: PersonaStatsBuilder = {
  buildStats: (data) => {
    const metrics =
      data.metrics as import('@/helpers/Neighbourhood/neighbourhoodPOITypes').StudentMetrics;
    return {
      stat1: {
        label: metrics.toUniversity.label,
        value: isValidValue(metrics.toUniversity.value) ? metrics.toUniversity.value : NA_VALUE,
        subtitle: '',
      },
      stat2: {
        label: metrics.mobility.label,
        value: isValidValue(metrics.mobility.value) ? metrics.mobility.value : NA_VALUE,
        subtitle: '',
      },
      stat3: {
        label: metrics.nightLinks.label,
        value: isValidValue(metrics.nightLinks.value) ? metrics.nightLinks.value : NA_VALUE,
        subtitle: '',
      },
    };
  },
};

const fallbackStatsBuilder: PersonaStatsBuilder = {
  buildStats: (data) => ({
    stat1: {
      label: 'Zone Type',
      value: data.keyPlaces.category || 'Residential',
      subtitle: '',
    },
    stat2: {
      label: 'Walkability',
      value: data.keyPlaces.walkability || NA_VALUE,
      subtitle: '',
    },
    stat3: {
      label: 'Nearby Places',
      value: data.keyPlaces.places.length.toString(),
      subtitle: '',
    },
  }),
};

/**
 * Build persona-specific stats using strategy pattern
 * Eliminates code duplication and improves testability
 */
function buildPersonaStats(data: NeighbourhoodPoiDescriptionResponse['data']) {
  const persona = data.keyPlaces.category;

  const builderMap: Record<string, PersonaStatsBuilder> = {
    Family: familyStatsBuilder,
    Professional: professionalStatsBuilder,
    Student: studentStatsBuilder,
  };

  const builder = builderMap[persona] || fallbackStatsBuilder;
  return builder.buildStats(data);
}

const ICON_NAMES = {
  TRAIN: 'train',
  BOOK: 'book-open',
  SHOPPING_CART: 'shopping-cart',
  COFFEE: 'coffee',
  UTENSILS: 'utensils',
  STETHOSCOPE: 'stethoscope',
  USERS: 'users',
  TREE: 'trees',
  CAR: 'car',
  TRAFFIC: 'traffic', // Keep 'traffic' as-is for NeighbourhoodIcon mapping
  BRIEFCASE: 'briefcase',
  SCHOOL: 'school',
  ACTIVITY: 'activity',
  SHIELD: 'shield',
  GRADUATION_CAP: 'graduation-cap',
  DOLLAR_SIGN: 'dollar-sign',
  LAPTOP: 'laptop',
  DUMBBELL: 'dumbbell',
  MAP_PIN: 'map-pin',
  BUILDING: 'building',
  PERSON_STANDING: 'person-standing',
  SHOPPING_BAG: 'shopping-bag',
  WIFI: 'wifi',
  HEART: 'heart',
  LIBRARY: 'library',
  HOME: 'home',
  INFO: 'info',
} as const;

function mapLifestyleDNAIconToComponent(icon: string, label = ''): string {
  const lowerIcon = icon.toLowerCase();
  const lowerLabel = label.toLowerCase();
  const iconMap: Record<string, string> = {
    community: ICON_NAMES.USERS,
    'community-focused': ICON_NAMES.USERS,
    community_focused: ICON_NAMES.USERS,
    leaf: ICON_NAMES.TREE,
    leafy: ICON_NAMES.TREE,
    green: ICON_NAMES.TREE,
    'green-living': ICON_NAMES.TREE,
    green_living: ICON_NAMES.TREE,
    oasis: ICON_NAMES.TREE,
    traffic: ICON_NAMES.TRAFFIC, // Changed from CAR to TRAFFIC
    calm: ICON_NAMES.SHIELD,
    'traffic-calmed': ICON_NAMES.SHIELD,
    traffic_calmed: ICON_NAMES.SHIELD,
    public_transport: ICON_NAMES.TRAIN,
    shop: ICON_NAMES.SHOPPING_CART,
    convenience: ICON_NAMES.SHOPPING_CART,
    convenient: ICON_NAMES.SHOPPING_CART,
    essentials: ICON_NAMES.SHOPPING_CART,
    family: ICON_NAMES.HOME,
    family_friendly: ICON_NAMES.HOME,
    'family-friendly': ICON_NAMES.HOME,
    work: ICON_NAMES.BRIEFCASE,
    study: ICON_NAMES.BOOK,
    activity: ICON_NAMES.ACTIVITY,
    shield: ICON_NAMES.SHIELD,
    'graduation-cap': ICON_NAMES.GRADUATION_CAP,
    'dollar-sign': ICON_NAMES.DOLLAR_SIGN,
    laptop: ICON_NAMES.LAPTOP,
    dumbbell: ICON_NAMES.DUMBBELL,
    'map-pin': ICON_NAMES.MAP_PIN,
    building: ICON_NAMES.BUILDING,
    coffee: ICON_NAMES.COFFEE,
    'person-standing': ICON_NAMES.PERSON_STANDING,
    'shopping-bag': ICON_NAMES.SHOPPING_BAG,
    wifi: ICON_NAMES.WIFI,
    heart: ICON_NAMES.HEART,
    home: ICON_NAMES.HOME,
  };

  if (iconMap[lowerIcon]) {
    return iconMap[lowerIcon];
  }

  if (lowerLabel.includes('community')) {
    return ICON_NAMES.USERS;
  }
  if (lowerLabel.includes('leaf') || lowerLabel.includes('green') || lowerLabel.includes('oasis')) {
    return ICON_NAMES.TREE;
  }
  if (
    lowerLabel.includes('traffic') ||
    lowerLabel.includes('calm') ||
    lowerLabel.includes('safe')
  ) {
    return ICON_NAMES.SHIELD;
  }
  if (
    lowerLabel.includes('convenien') ||
    lowerLabel.includes('essentials') ||
    lowerLabel.includes('shopping')
  ) {
    return ICON_NAMES.SHOPPING_CART;
  }
  if (lowerLabel.includes('family')) {
    return ICON_NAMES.HOME;
  }

  return ICON_NAMES.INFO;
}

function mapKeyPlaceIconToComponent(icon: string): string {
  const lowerIcon = icon.toLowerCase();
  const iconMap: Record<string, string> = {
    school: ICON_NAMES.SCHOOL,
    park: ICON_NAMES.TREE,
    doctor: ICON_NAMES.STETHOSCOPE,
    public_transport: ICON_NAMES.TRAIN,
    food: ICON_NAMES.UTENSILS,
    work: ICON_NAMES.BRIEFCASE,
    study: ICON_NAMES.BOOK,
    community: ICON_NAMES.USERS,
    supermarket: ICON_NAMES.SHOPPING_CART,
    pharmacy: ICON_NAMES.STETHOSCOPE,
    station: ICON_NAMES.TRAIN,
    playground: ICON_NAMES.TREE,
    kindergarten: ICON_NAMES.SCHOOL,
    sports_centre: ICON_NAMES.DUMBBELL,
    cafe: ICON_NAMES.COFFEE,
    coworking: ICON_NAMES.LAPTOP,
    university: ICON_NAMES.GRADUATION_CAP,
    library: ICON_NAMES.LIBRARY,
    fast_food: ICON_NAMES.UTENSILS,
  };

  return iconMap[lowerIcon] || ICON_NAMES.MAP_PIN;
}
